import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { actions } from '../../state';
import { config } from '../../api/config';
import { formatDate, formatFilename } from '../../utils';
import {
  Button,
  ButtonDownload,
  Header,
  HeaderActions,
  Table,
  TableCell,
  TableRow,
  Title,
} from '../../components';
import {
  getAssignedLocations,
  getBannerSchedule,
  getBannerStatus,
  isAllDayTimeRange,
  mapLocationLabel,
} from './banners.utils';
import styles from './banners.module.scss';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const Banners = ({
  dispatch,
  history,
  banners: { banners, total, loading },
  xlsLoading,
}) => {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  const fetchBanners = useCallback(
    filter => {
      dispatch(actions.banners.getBannersAction(filter));
    },
    [dispatch]
  );

  const clearBanners = useCallback(() => {
    dispatch(actions.banners.clearBannersAction());
  }, [dispatch]);

  useEffect(() => () => clearBanners(), [clearBanners]);

  useEffect(() => {
    const offset = (page - 1) * PAGE_SIZE;
    fetchBanners({ offset });
  }, [fetchBanners, page]);

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const xlsExport = () => {
    const filename = formatFilename(config.PAGES.banners);
    dispatch(actions.banners.exportBannersAction(filename));
  };

  const handlePriorityChange = (event, id, direction) => {
    event.preventDefault();
    event.stopPropagation();

    const offset = (page - 1) * PAGE_SIZE;
    dispatch(
      actions.banners.updateBannerPriorityAction(id, direction, { offset })
    );
  };

  const tableHeader = [
    'Statusas',
    'Pavadinimas',
    'Galioja nuo',
    'Galioja iki',
    'Laikas',
    'Priskirta',
    '',
  ];

  const renderTable = () =>
    banners.map((banner, index) => {
      const status = getBannerStatus(banner);
      const assignedLocations = getAssignedLocations(banner)
        .map(mapLocationLabel)
        .join(', ');
      const schedule = getBannerSchedule(banner);
      const dateInterval = [banner.published_at, banner.unpublished_at];
      const isFirstRow = page === 1 && index === 0;
      const isLastRow = page === pageCount && index === banners.length - 1;

      return (
        <TableRow
          key={banner.id}
          onClick={() => history.push(config.APP_PATHS.editBanner(banner.id))}
        >
          <TableCell>
            <span style={{ color: status.color }}>{status.label}</span>
          </TableCell>
          <TableCell>
            {banner.title_lt ||
              banner.title_en ||
              banner.title_ru ||
              banner.name}
          </TableCell>
          <TableCell>
            {dateInterval[0] ? formatDate(dateInterval[0]) : '-'}
          </TableCell>
          <TableCell>
            {dateInterval[1] ? formatDate(dateInterval[1]) : '-'}
          </TableCell>
          <TableCell>
            {schedule.timeFrom &&
            schedule.timeTo &&
            !isAllDayTimeRange(schedule.timeFrom, schedule.timeTo)
              ? `${schedule.timeFrom}-${schedule.timeTo}`
              : 'Visa diena'}
          </TableCell>
          <TableCell>{assignedLocations || '-'}</TableCell>
          <TableCell>
            <div className={styles.priorityActions}>
              <button
                className={styles.priorityButton}
                type="button"
                disabled={loading || isFirstRow}
                onClick={event => handlePriorityChange(event, banner.id, 'up')}
              >
                ↑
              </button>
              <button
                className={styles.priorityButton}
                type="button"
                disabled={loading || isLastRow}
                onClick={event =>
                  handlePriorityChange(event, banner.id, 'down')
                }
              >
                ↓
              </button>
            </div>
          </TableCell>
        </TableRow>
      );
    });

  return (
    <section>
      <Header>
        <Title total={total}>{config.PAGES.banners}</Title>
        <HeaderActions>
          <ButtonDownload onClick={xlsExport} loading={xlsLoading} />
          <Button color="primary" url={config.APP_PATHS.addBanner}>
            Sukurti banerį
          </Button>
        </HeaderActions>
      </Header>

      <Table
        header={tableHeader}
        isEmpty={!banners.length}
        loading={loading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </section>
  );
};

const mapStateToProps = state => ({
  banners: state.banners,
  xlsLoading: state.download.xlsLoading,
});

Banners.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  banners: PropTypes.shape({
    banners: PropTypes.arrayOf(PropTypes.object).isRequired,
    total: PropTypes.number.isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  xlsLoading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(Banners);
